import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Monogram from "@/components/Monogram";
import LangToggle from "@/components/header/LangToggle";
import UserMenu from "@/components/header/UserMenu";
import Nav from "@/components/header/Nav";
import MobileMenu from "@/components/header/MobileMenu";
import { useLang } from "@/lib/i18n";

/**
 * Site header — composition layer. Owns scroll/mobile-open state and the
 * isSolid/textTone derivation; delegates the actual UI surfaces to
 *   Nav         — desktop primary nav
 *   MobileMenu  — fullscreen drawer
 *   UserMenu    — account dropdown
 *   LangToggle  — EN/ES switch
 */
export default function Header() {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const isSolid = !isHome || scrolled;
  const textTone = isSolid ? "text-charcoal" : "text-ivory";
  const userTone = isSolid ? "charcoal" : "ivory";

  // TODO: Re-enable when catalog > 30 experiences (mirrors SHOW_COLLECTION in Home.jsx).
  // Route `/the-collection` stays mounted in App.jsx so external links / bookmarks
  // keep resolving — we only hide the visible entry from the nav.
  const SHOW_COLLECTION_NAV = false;

  const navItems = [
    { to: "/spain", label: t.nav.spain, slug: "spain" },
    { to: "/italy", label: t.nav.italy, slug: "italy" },
    { to: "/portugal", label: t.nav.portugal, slug: "portugal" },
    { to: "/experiences", label: t.nav.experiences, slug: "experiences" },
    ...(SHOW_COLLECTION_NAV
      ? [{ to: "/the-collection", label: t.nav.collection, slug: "collection" }]
      : []),
    { to: "/contact", label: t.nav.contact, slug: "contact" },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        isSolid
          ? "bg-ivory/95 backdrop-blur-md border-b border-mist"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex items-center justify-between px-6 md:px-10 h-16 md:h-20">
        <Link
          to="/"
          aria-label="Viaja de Verdad — Home"
          className="flex items-center gap-3"
          data-testid="header-logo"
        >
          <Monogram
            size={isSolid ? "smLight" : "sm"}
            tone={isSolid ? "light" : "dark"}
          />
          <span
            className={`hidden md:inline-block wordmark ${textTone} transition-colors duration-500 text-sm`}
            style={{ letterSpacing: "0.24em" }}
          >
            Viaja de Verdad.
          </span>
        </Link>

        <Nav items={navItems} textTone={textTone} />

        <div className="hidden lg:flex items-center gap-5">
          <LangToggle className={`${textTone} transition-colors duration-500`} />
          <UserMenu tone={userTone} />
        </div>

        <button
          type="button"
          className={`lg:hidden inline-flex items-center justify-center w-10 h-10 ${textTone}`}
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          onClick={() => setMobileOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {mobileOpen && <MobileMenu items={navItems} />}
    </header>
  );
}
